'use client';

/**
 * Building blocks for organiser sign in, sign up, the application and the
 * holding screen: the page shell (header, columns, footer), fields, buttons,
 * the provider buttons, the code field and the small notes the steps share.
 *
 * Colour comes only from the --fvd-* tokens, so every screen follows the
 * theme. Anything with a hover, focus or breakpoint state is a Tailwind class,
 * because an inline style would outrank the state and the breakpoint.
 */

import Image from 'next/image';
import Link from 'next/link';
import { forwardRef, type ReactNode, type InputHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { PASSWORD_MIN_LENGTH } from '@/lib/auth/auth-flow';
import { FitventMark } from '@/components/design/FitventMark';

/* ── Page shell ─────────────────────────────────────────────────────────── */

export function AuthPage({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--fvd-canvas)',
        color: 'var(--fvd-ink)',
        fontFamily: 'var(--fvd-font-body)',
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      {children}
    </div>
  );
}

export function AuthHeader({
  prompt,
  action,
}: {
  /** The words before the switch button, e.g. "New to Fitvent?", or the email once part-way in. */
  prompt?: ReactNode;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <header
      className="flex items-center gap-3 px-4 py-4 min-[600px]:gap-4 min-[600px]:px-7 min-[600px]:py-5"
      style={{ flex: 'none' }}
    >
      <Link
        href="/"
        aria-label="Fitvent home"
        style={{ display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none', flex: 'none' }}
      >
        <FitventMark size={20} />
        <span className="fvd-wordmark" style={{ fontSize: 17 }}>
          fitvent
        </span>
      </Link>
      <span style={{ flex: 1 }} />
      {prompt && (
        <span
          className="truncate max-[400px]:hidden"
          style={{ minWidth: 0, fontSize: 13, color: 'var(--fvd-ink-secondary)' }}
        >
          {prompt}
        </span>
      )}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          // 44 tall on a phone (a tap target), 36 from 768 up. A class, so the breakpoint applies.
          className="fvd-hoverable h-[44px] min-[768px]:h-[36px]"
          style={{
            flex: 'none',
            padding: '0 13px',
            border: '1px solid var(--fvd-border-control)',
            borderRadius: 8,
            background: 'var(--fvd-canvas)',
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--fvd-ink)',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {action.label}
        </button>
      )}
    </header>
  );
}

/** The footer links: a 44px tap target on a phone, plain text from 768 up. */
const footerLinkClass = 'hover:underline inline-flex items-center max-[767px]:min-h-[44px]';

export function AuthFooter() {
  const linkStyle = { color: 'var(--fvd-ink-muted)', textDecoration: 'none' } as const;
  return (
    <footer
      className="flex flex-wrap items-center gap-[18px] px-4 py-5 min-[600px]:px-7"
      style={{ flex: 'none', fontSize: 12.5, color: 'var(--fvd-ink-muted)' }}
    >
      <span>© {new Date().getFullYear()} Fitvent</span>
      <span style={{ flex: 1 }} />
      <Link href="/terms" style={linkStyle} className={footerLinkClass}>
        Terms
      </Link>
      <Link href="/privacy" style={linkStyle} className={footerLinkClass}>
        Privacy
      </Link>
    </footer>
  );
}

/**
 * One centred column (sign in, the code step, the holding screen), or the
 * form plus a grey panel running to the right edge (the first sign-up step
 * and the application). Below 980px the panel hides and it is one column.
 */
export function AuthMain({
  width = 380,
  panel,
  children,
}: {
  width?: number;
  panel?: ReactNode;
  children: ReactNode;
}) {
  if (!panel) {
    return (
      <main className="px-4 pb-10 pt-8 min-[600px]:px-5 min-[600px]:pt-14" style={{ flex: 1 }}>
        <div style={{ width: '100%', maxWidth: width, margin: '0 auto' }}>{children}</div>
      </main>
    );
  }
  return (
    <main
      className={[
        'grid grid-cols-[minmax(0,1fr)] gap-14 px-4 pb-10 pt-8 min-[600px]:px-5 min-[600px]:pt-10',
        'min-[981px]:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] min-[981px]:py-2 min-[981px]:pl-10 min-[981px]:pr-0',
        'items-start',
      ].join(' ')}
      style={{ flex: 1 }}
    >
      {/* The form's first line sits level with the panel's heading (the
          panel's own 44px top padding), on every step: centred, a short step
          floated halfway down beside a picture that starts at the top. */}
      <div className="min-[981px]:pt-[44px]" style={{ width: '100%', maxWidth: width, justifySelf: 'center' }}>{children}</div>
      {panel}
    </main>
  );
}

/** The grey right-hand panel. Hidden at 980px and below. */
export function SidePanel({ children, picture }: { children: ReactNode; picture?: boolean }) {
  return (
    <div
      className="hidden min-[981px]:flex"
      style={{
        flexDirection: 'column',
        // The picture fills its column. The application's info panel stays in
        // view while the longer form scrolls past it (as it scrolled away,
        // the right side was left an empty grey block).
        alignSelf: picture ? 'stretch' : 'start',
        position: picture ? undefined : 'sticky',
        top: picture ? undefined : 16,
        minHeight: picture ? 560 : undefined,
        borderRadius: '16px 0 0 16px',
        background: 'var(--fvd-switcher-bg)',
        overflow: 'hidden',
        padding: picture ? '44px 0 0 44px' : '44px 48px 40px 44px',
        gap: picture ? 0 : 30,
      }}
    >
      {children}
    </div>
  );
}

/** Sign-up step 1: the organiser dashboard picture. */
export function OrganiserArtPanel() {
  return (
    <SidePanel picture>
      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--fvd-ink)' }}>Run the whole event from one screen</div>
      <div style={{ fontSize: 13, color: 'var(--fvd-ink-secondary)', marginTop: 5, maxWidth: '40ch', lineHeight: 1.55 }}>
        Entries, check-in, sponsors and payouts, with the few things that need you today at the top.
      </div>
      <Image
        src="/auth/auth-organiser-dashboard.png"
        alt="The Fitvent organiser dashboard"
        width={2560}
        height={1600}
        sizes="(min-width: 981px) 70vw, 1px"
        priority
        style={{
          display: 'block',
          width: '125%',
          maxWidth: 'none',
          height: 'auto',
          marginTop: 30,
          borderRadius: '12px 0 0 0',
          boxShadow: 'var(--fvd-shadow-modal), 0 0 0 1px var(--fvd-hairline)',
        }}
      />
    </SidePanel>
  );
}

/* ── Progress ───────────────────────────────────────────────────────────── */

export function SignUpProgress({ step }: { step: 1 | 2 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22 }}>
      <div style={{ display: 'flex', gap: 4 }} aria-hidden="true">
        {[1, 2].map((n) => (
          <span
            key={n}
            style={{
              width: 28,
              height: 4,
              borderRadius: 99,
              background: n <= step ? 'var(--fvd-teal)' : 'var(--fvd-hairline-strong)',
            }}
          />
        ))}
      </div>
      <span style={{ fontSize: 12.5, color: 'var(--fvd-ink-muted)' }}>
        {step === 1 ? 'Step 1 of 2 · Your account' : 'Step 2 of 2 · Your organisation'}
      </span>
    </div>
  );
}

/* ── Type ───────────────────────────────────────────────────────────────── */

export function AuthTitle({ children, large, gap = 0 }: { children: ReactNode; large?: boolean; gap?: number }) {
  return (
    <h1
      className={large ? 'text-[28px] min-[600px]:text-[34px]' : 'text-[24px]'}
      style={{
        margin: `${gap}px 0 0`,
        fontWeight: 600,
        letterSpacing: large ? '-.032em' : '-.028em',
        lineHeight: large ? 1.12 : 1.2,
        textWrap: 'balance',
        color: 'var(--fvd-ink)',
      }}
    >
      {children}
    </h1>
  );
}

export function AuthIntro({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p
      className={className}
      style={{ margin: '9px 0 0', fontSize: 14, color: 'var(--fvd-ink-secondary)', lineHeight: 1.55, textWrap: 'pretty' }}
    >
      {children}
    </p>
  );
}

/** The tile above the code, done and holding steps. */
export function AuthIconTile({ children, round, tone = 'teal' }: { children: ReactNode; round?: boolean; tone?: 'teal' | 'amber' | 'danger' }) {
  const bg = tone === 'amber' ? 'var(--fvd-amber-tint)' : tone === 'danger' ? 'var(--fvd-danger-tint)' : 'var(--fvd-teal-tint)';
  const fg = tone === 'amber' ? 'var(--fvd-amber-text)' : tone === 'danger' ? 'var(--fvd-danger)' : 'var(--fvd-teal)';
  return (
    <div
      aria-hidden="true"
      style={{
        width: 40,
        height: 40,
        borderRadius: round ? '50%' : 10,
        background: bg,
        color: fg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </div>
  );
}

/* ── Fields ─────────────────────────────────────────────────────────────── */

/**
 * Border and focus ring as classes: the ring and teal edge are focus states
 * and an inline border colour would beat them. 16px text and 44px height on
 * a phone (Safari zooms anything smaller), 14px and 42px from 768px.
 */
export function fieldClass(invalid: boolean, extra = ''): string {
  return [
    'w-full box-border outline-none border transition-[border-color,box-shadow] duration-100',
    'h-[44px] text-[16px] min-[768px]:h-[42px] min-[768px]:text-[14px]',
    invalid ? 'border-[color:var(--fvd-danger)]' : 'border-[color:var(--fvd-border-control)]',
    'focus:border-[color:var(--fvd-teal)]',
    'focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--fvd-teal)_14%,transparent)]',
    'placeholder:text-[color:var(--fvd-ink-disabled)]',
    extra,
  ].join(' ');
}

export const fieldStyle = {
  padding: '0 13px',
  borderRadius: 9,
  background: 'var(--fvd-canvas)',
  color: 'var(--fvd-ink)',
  fontFamily: 'inherit',
  minWidth: 0,
} as const;

export function FieldLabel({ htmlFor, children, aside }: { htmlFor?: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 7 }}>
      <label htmlFor={htmlFor} style={{ fontSize: 13, fontWeight: 500, color: 'var(--fvd-ink)' }}>
        {children}
      </label>
      {aside && (
        <>
          <span style={{ flex: 1 }} />
          {aside}
        </>
      )}
    </div>
  );
}

export function FieldMessage({ id, tone, children }: { id?: string; tone: 'error' | 'hint' | 'done'; children: ReactNode }) {
  return (
    <div
      id={id}
      role={tone === 'error' ? 'alert' : undefined}
      style={{
        fontSize: 12.5,
        lineHeight: 1.5,
        marginTop: 7,
        color: tone === 'error' ? 'var(--fvd-danger)' : tone === 'done' ? 'var(--fvd-teal-text)' : 'var(--fvd-ink-muted)',
      }}
    >
      {children}
    </div>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean };

export const TextInput = forwardRef<HTMLInputElement, InputProps>(function TextInput({ invalid, className, style, ...rest }, ref) {
  return <input ref={ref} {...rest} className={fieldClass(!!invalid, className)} style={{ ...fieldStyle, ...style }} />;
});

/** A password field with a Show / Hide toggle inside it. */
export function PasswordInput({
  id,
  value,
  onChange,
  autoComplete,
  invalid,
  shown,
  onToggle,
  onEnter,
  describedBy,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: 'current-password' | 'new-password';
  invalid?: boolean;
  shown: boolean;
  onToggle: () => void;
  onEnter?: () => void;
  describedBy?: string;
}) {
  return (
    <div style={{ position: 'relative' }}>
      <TextInput
        id={id}
        type={shown ? 'text' : 'password'}
        autoComplete={autoComplete}
        value={value}
        invalid={invalid}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onEnter) {
            e.preventDefault();
            onEnter();
          }
        }}
        style={{ paddingRight: 64 }}
      />
      <button
        type="button"
        onClick={onToggle}
        aria-label={shown ? 'Hide password' : 'Show password'}
        className="hover:bg-[color:var(--fvd-hover-tint)] hover:text-[color:var(--fvd-ink)]"
        style={{
          position: 'absolute',
          right: 6,
          top: '50%',
          transform: 'translateY(-50%)',
          height: 32,
          padding: '0 9px',
          border: 'none',
          borderRadius: 6,
          background: 'transparent',
          fontFamily: 'inherit',
          fontSize: 12.5,
          fontWeight: 500,
          color: 'var(--fvd-ink-secondary)',
          cursor: 'pointer',
        }}
      >
        {shown ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

/**
 * The one code field. Digits only (anything else is dropped as they type or
 * paste, so no maxLength: that would cut a pasted "482 913" short before the
 * space is removed), and the browser can fill it from the email or message.
 */
export function CodeInput({
  value,
  onChange,
  onEnter,
  invalid,
  describedBy,
  autoFocus,
}: {
  value: string;
  onChange: (raw: string) => void;
  onEnter?: () => void;
  invalid?: boolean;
  describedBy?: string;
  autoFocus?: boolean;
}) {
  return (
    <input
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]*"
      aria-label="6-digit code"
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      autoFocus={autoFocus}
      value={value}
      placeholder="000000"
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && onEnter) {
          e.preventDefault();
          onEnter();
        }
      }}
      className={[
        'w-full box-border outline-none border transition-[border-color,box-shadow] duration-100',
        invalid ? 'border-[color:var(--fvd-danger)]' : 'border-[color:var(--fvd-border-control)]',
        'focus:border-[color:var(--fvd-teal)]',
        'focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--fvd-teal)_14%,transparent)]',
        'placeholder:text-[color:var(--fvd-ink-disabled)]',
      ].join(' ')}
      style={{
        height: 54,
        marginTop: 24,
        padding: '0 16px',
        borderRadius: 10,
        background: 'var(--fvd-canvas)',
        fontFamily: 'var(--fvd-font-mono)',
        fontSize: 24,
        letterSpacing: '.42em',
        textAlign: 'center',
        color: 'var(--fvd-ink)',
      }}
    />
  );
}

/** A tick box drawn in the system's language (the native one ignores the theme). */
export function TickBox({ checked, onChange, children }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', minHeight: 36 }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className="peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[color:var(--fvd-teal)]"
        style={{
          flex: 'none',
          width: 16,
          height: 16,
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1.5px solid ${checked ? 'var(--fvd-teal)' : 'var(--fvd-ink-disabled)'}`,
          background: checked ? 'var(--fvd-teal)' : 'transparent',
          color: 'var(--fvd-teal-on)',
        }}
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: checked ? 1 : 0 }}>
          <path d="m5 12.5 4.5 4.5L19 7" />
        </svg>
      </span>
      <span style={{ fontSize: 13, color: 'var(--fvd-ink)' }}>{children}</span>
    </label>
  );
}

/* ── Buttons ────────────────────────────────────────────────────────────── */

/**
 * The full-width primary. When it can't be used yet it is grey (not faded
 * teal), and whatever sits beside it says what is missing. A request in
 * flight keeps the teal: the action was valid, it is just busy.
 */
export function PrimaryButton({
  children,
  disabled,
  loading,
  type = 'submit',
  onClick,
  marginTop = 16,
  full = true,
  compact,
}: {
  children: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  type?: 'submit' | 'button';
  onClick?: () => void;
  marginTop?: number;
  full?: boolean;
  compact?: boolean;
}) {
  const off = disabled || loading;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={off}
      aria-busy={loading || undefined}
      className={[
        off ? '' : 'fvd-hoverable-solid',
        compact ? 'h-[44px] min-[768px]:h-[38px]' : 'h-[48px] min-[768px]:h-[42px]',
      ].join(' ')}
      style={{
        width: full ? '100%' : undefined,
        flex: full ? undefined : 'none',
        padding: full ? undefined : '0 15px',
        marginTop,
        border: 'none',
        borderRadius: 9,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        whiteSpace: 'nowrap',
        fontFamily: 'inherit',
        fontSize: compact ? 13.5 : 14,
        fontWeight: 500,
        cursor: off ? 'not-allowed' : 'pointer',
        background: disabled && !loading ? 'var(--fvd-neutral-chip)' : 'var(--fvd-teal-solid)',
        color: disabled && !loading ? 'var(--fvd-ink-disabled)' : 'var(--fvd-teal-solid-on)',
      }}
    >
      {loading && <Loader2 className="animate-spin" width={15} height={15} aria-hidden="true" />}
      {children}
    </button>
  );
}

export function SecondaryButton({
  children,
  onClick,
  disabled,
  loading,
  marginTop = 0,
  full = true,
  compact,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  marginTop?: number;
  full?: boolean;
  compact?: boolean;
  type?: 'button' | 'submit';
}) {
  const off = disabled || loading;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={off}
      aria-busy={loading || undefined}
      className={[
        off ? '' : 'fvd-hoverable',
        compact ? 'h-[44px] min-[768px]:h-[38px]' : 'h-[48px] min-[768px]:h-[42px]',
      ].join(' ')}
      style={{
        width: full ? '100%' : undefined,
        flex: full ? undefined : 'none',
        padding: full ? undefined : '0 14px',
        marginTop,
        border: '1px solid var(--fvd-border-control)',
        borderRadius: 9,
        background: 'var(--fvd-canvas)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        whiteSpace: 'nowrap',
        fontFamily: 'inherit',
        fontSize: compact ? 13.5 : 14,
        fontWeight: 500,
        color: off ? 'var(--fvd-ink-disabled)' : 'var(--fvd-ink)',
        cursor: off ? 'not-allowed' : 'pointer',
      }}
    >
      {loading && <Loader2 className="animate-spin" width={15} height={15} aria-hidden="true" />}
      {children}
    </button>
  );
}

/** An inline text action ("Send it again", "Forgot it?"). */
export function TextAction({
  children,
  onClick,
  disabled,
  muted,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  muted?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      // A 44px tap target on a phone; inline-flex keeps it on the text's baseline.
      className="fvd-link inline-flex items-center max-[767px]:min-h-[44px]"
      style={{
        padding: 0,
        border: 'none',
        background: 'transparent',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        fontWeight: 500,
        color: disabled ? 'var(--fvd-ink-disabled)' : muted ? 'var(--fvd-ink-secondary)' : 'var(--fvd-ink)',
        textDecoration: disabled ? 'none' : undefined,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

/** The email as a chip at the top of a step; clicking it goes back to the email step. */
export function EmailChip({ email, onClick }: { email: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Change email, ${email}`}
      className="fvd-hoverable"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        maxWidth: '100%',
        height: 32,
        padding: '0 11px 0 9px',
        border: '1px solid var(--fvd-border-control)',
        borderRadius: 99,
        background: 'var(--fvd-canvas)',
        fontFamily: 'inherit',
        fontSize: 13,
        color: 'var(--fvd-ink)',
        cursor: 'pointer',
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none', color: 'var(--fvd-ink-secondary)' }}>
        <path d="M19 12H5.5" />
        <path d="m11 5.5-6 6.5 6 6.5" />
      </svg>
      <span className="truncate">{email}</span>
    </button>
  );
}

/* ── Provider buttons ───────────────────────────────────────────────────── */

export function GoogleIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.5 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.9a5.04 5.04 0 0 1-2.19 3.31v2.76h3.54c2.07-1.9 3.25-4.72 3.25-8.08Z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.54-2.76c-.98.66-2.24 1.05-3.74 1.05-2.87 0-5.3-1.94-6.17-4.55H2.18v2.85A11 11 0 0 0 12 23Z" />
      <path fill="#FBBC05" d="M5.83 14.08a6.6 6.6 0 0 1 0-4.16V7.07H2.18a11 11 0 0 0 0 9.86l3.65-2.85Z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.15-3.15A10.97 10.97 0 0 0 12 1 11 11 0 0 0 2.18 7.07l3.65 2.85C6.7 7.32 9.13 5.38 12 5.38Z" />
    </svg>
  );
}

export function AppleIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.37 12.62c-.02-2.3 1.88-3.4 1.97-3.46-1.07-1.57-2.74-1.78-3.33-1.8-1.42-.14-2.77.83-3.49.83-.72 0-1.83-.81-3.01-.79-1.55.02-2.98.9-3.78 2.29-1.61 2.8-.41 6.94 1.16 9.21.77 1.11 1.68 2.36 2.88 2.31 1.16-.05 1.6-.75 3-.75s1.79.75 3.01.72c1.25-.02 2.03-1.13 2.79-2.25.88-1.29 1.24-2.53 1.26-2.6-.03-.01-2.42-.93-2.46-3.71ZM14.08 5.87c.64-.77 1.07-1.85.95-2.92-.92.04-2.03.61-2.69 1.38-.59.68-1.11 1.77-.97 2.82 1.02.08 2.07-.52 2.71-1.28Z" />
    </svg>
  );
}

export function ProviderButtons({
  onGoogle,
  onApple,
  busy,
}: {
  onGoogle: () => void;
  onApple: () => void;
  busy: 'google' | 'apple' | null;
}) {
  return (
    <>
      <SecondaryButton onClick={onGoogle} loading={busy === 'google'} disabled={!!busy && busy !== 'google'}>
        {busy !== 'google' && <GoogleIcon />}
        Continue with Google
      </SecondaryButton>
      <SecondaryButton onClick={onApple} loading={busy === 'apple'} disabled={!!busy && busy !== 'apple'}>
        {busy !== 'apple' && <AppleIcon />}
        Continue with Apple
      </SecondaryButton>
    </>
  );
}

export function OrDivider() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0' }}>
      <span style={{ flex: 1, height: 1, background: 'var(--fvd-hairline)' }} />
      <span style={{ fontSize: 12, color: 'var(--fvd-ink-muted)' }}>or</span>
      <span style={{ flex: 1, height: 1, background: 'var(--fvd-hairline)' }} />
    </div>
  );
}

/* ── The box below the form ─────────────────────────────────────────────── */

/** "Entering an event?" with a link to myfitvent (athlete accounts are separate). */
export function AthleteNote({ actionLabel }: { actionLabel: 'Sign in to myfitvent' | 'Join myfitvent' }) {
  return (
    <Link
      href={actionLabel === 'Join myfitvent' ? '/hub/login?mode=signup' : '/hub/login'}
      className="group"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        marginTop: 26,
        // Flush with the form's left edge, like every field above it.
        padding: '14px 0 0',
        borderTop: '1px solid var(--fvd-hairline)',
        textDecoration: 'none',
        flexWrap: 'wrap',
      }}
    >
      <span style={{ flex: '1 1 200px', minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--fvd-ink)' }}>Entering an event?</span>
        <span style={{ display: 'block', fontSize: 12.5, color: 'var(--fvd-ink-muted)', marginTop: 2, lineHeight: 1.5 }}>
          Your tickets, start times and results live in myfitvent.
        </span>
      </span>
      <span
        className="underline decoration-[var(--fvd-border-control)] decoration-1 underline-offset-[3px] group-hover:decoration-current"
        style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: 'var(--fvd-ink)', whiteSpace: 'nowrap' }}
      >
        {actionLabel}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M5 12h13" />
          <path d="m12.5 6 6 6-6 6" />
        </svg>
      </span>
    </Link>
  );
}

/* ── Icons used by more than one step ──────────────────────────────────── */

export function MailIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3.5" y="5.5" width="17" height="13" rx="2.5" />
      <path d="m4 7 8 5.5L20 7" />
    </svg>
  );
}

export function TickIcon({ size = 20, width = 2.4 }: { size?: number; width?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round">
      <path d="m5 12.5 4.5 4.5L19 7" />
    </svg>
  );
}

export function ClockIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  );
}

export function MessageIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 6.5h15v9a1 1 0 0 1-1 1H9l-4.5 3.5V6.5Z" />
    </svg>
  );
}

export function CrossIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 7 7 17M7 7l10 10" />
    </svg>
  );
}

/** "At least 8 characters", ticking green once it is met. */
export function PasswordRule({ met }: { met: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        marginTop: 8,
        fontSize: 12.5,
        color: met ? 'var(--fvd-teal-text)' : 'var(--fvd-ink-muted)',
      }}
    >
      {/* Unmet: a plain ring (it read as an info button as an "i"); met: a tick. */}
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={met ? 2.6 : 2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {met ? <path d="m5 12.5 4.5 4.5L19 7" /> : <circle cx="12" cy="12" r="8" />}
      </svg>
      <span>
        At least {PASSWORD_MIN_LENGTH} characters
        <span className="sr-only">{met ? ', done' : ', not yet'}</span>
      </span>
    </div>
  );
}
